import React, { useMemo } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import Breadcrumbs from '@mui/material/Breadcrumbs'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import { useSelector } from 'react-redux'
import type { RootState } from '@/src/store'
import { buildBhyveAppCrumbs } from './bhyveAppCrumbsModel'
import { FranchiseBreadcrumbStatus } from './FranchiseBreadcrumbStatus'
import { RestaurantBreadcrumbStatus } from './RestaurantBreadcrumbStatus'
import { bhyveNavTokens as T } from './bhyveNavTokens'

export type BhyveAppBreadcrumbsProps = {
  suppressBottomBorder?: boolean
}

export const BhyveAppBreadcrumbs: React.FC<BhyveAppBreadcrumbsProps> = ({ suppressBottomBorder = false }) => {
  const router = useRouter()
  const pathname = router.pathname
  const pathForSegments = router.asPath.split('?')[0] ?? router.pathname
  const { id: idParam } = router.query

  const restaurantRecord = useSelector((s: RootState) => {
    const { restoData, restoDataWithUsers } = s.gyg365RestoList

    return restoDataWithUsers ?? restoData
  })

  const franchiseRecord = useSelector((s: RootState) => s.gyg365FranchiseList.franchiseData)

  const crumbs = useMemo(
    () => buildBhyveAppCrumbs({ pathname, pathForSegments, idParam, restaurantRecord, franchiseRecord }),
    [pathname, pathForSegments, idParam, restaurantRecord, franchiseRecord]
  )

  if (crumbs.length === 0) return null

  return (
    <Box
      data-bhyve-breadcrumbs
      sx={{
        px: 2,
        pt: 2,
        pb: 1,
        flexShrink: 0,
        bgcolor: '#fff',
        borderBottom: suppressBottomBorder ? 'none' : `1px solid ${T.secondaryBarBorder}`
      }}
    >
      <Breadcrumbs aria-label='breadcrumb' separator='/' sx={{ '& .MuiBreadcrumbs-separator': { mx: 1 } }}>
        {crumbs.map((c, i) => {
          const isLast = i === crumbs.length - 1

          if (!isLast && c.href) {
            return (
              <Link key={`${c.label}-${i}`} href={c.href} style={{ textDecoration: 'none' }}>
                <Typography
                  variant='h2'
                  component='span'
                  sx={{
                    fontSize: { xs: 22, sm: 26 },
                    fontWeight: 700,
                    color: T.sidebarTextMuted,
                    '&:hover': { color: T.sidebarText }
                  }}
                >
                  {c.label}
                </Typography>
              </Link>
            )
          }

          if (c.showRestaurantStatus && restaurantRecord) {
            return (
              <Box
                key={`${c.label}-${i}`}
                component='span'
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1.5,
                  flexWrap: 'wrap',
                  minWidth: 0
                }}
              >
                <Typography
                  variant='h2'
                  component='span'
                  sx={{ fontSize: { xs: 22, sm: 26 }, fontWeight: 700, color: T.sidebarText }}
                >
                  {c.label}
                </Typography>
                <RestaurantBreadcrumbStatus restaurant={restaurantRecord} />
              </Box>
            )
          }

          if (c.showFranchiseStatus && franchiseRecord) {
            return (
              <Box
                key={`${c.label}-${i}`}
                component='span'
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1.5,
                  flexWrap: 'wrap',
                  minWidth: 0
                }}
              >
                <Typography
                  variant='h2'
                  component='span'
                  sx={{ fontSize: { xs: 22, sm: 26 }, fontWeight: 700, color: T.sidebarText }}
                >
                  {c.label}
                </Typography>
                <FranchiseBreadcrumbStatus franchise={franchiseRecord} />
              </Box>
            )
          }

          return (
            <Typography
              key={`${c.label}-${i}`}
              variant='h2'
              component='span'
              sx={{
                fontSize: { xs: 22, sm: 26 },
                fontWeight: 700,
                color: isLast ? T.sidebarText : T.sidebarTextMuted
              }}
            >
              {c.label}
            </Typography>
          )
        })}
      </Breadcrumbs>
    </Box>
  )
}
